import React from "react";
import { useDispatch } from "react-redux";
import { Box, Typography, IconButton, Button, Paper } from "@mui/material";
import { Add, QuestionAnswer } from "@mui/icons-material";
import Langs from "../../../../app/lang/langs";
import { setAddQuestionModalVisibility, setAddQuestionFromTree } from "../../vcompSlice";
import QuestionsTree from "../../QuestionsTree";

/**
 * MobileQuestionsTab - Mobile-friendly version of QuestionsTab.
 * Renders the project questions bank with a scrollable tree view
 * and sticky bottom navigation.
 */
export default function MobileQuestionsTab({
    isMobile,
    mode,
    activeIndex,
    setActiveIndex,
    onCreateProject,
    selectedKeys,
    onSelectionChange,
    handleAddDefaultProjectForm,
}) {
    const dispatch = useDispatch();

    const handleAddQuestion = () => {
        dispatch(setAddQuestionModalVisibility(true));
        dispatch(setAddQuestionFromTree(true));
    };

    return (
        <Box
            sx={{
                mt: 0,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                position: "relative",
            }}
        >
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "var(--surface-card)",
                    borderRadius: 0,
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <QuestionAnswer
                        sx={{ color: "#FF9015", fontSize: 22 }}
                    />
                    <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, fontSize: "1rem" }}
                    >
                        <Langs str="PROJECT_QUESTIONS_BANK" />
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleAddQuestion}
                    sx={{
                        backgroundColor: "#FF9015",
                        color: "#fff",
                        width: 36,
                        height: 36,
                        "&:hover": {
                            backgroundColor: "#e07e0d",
                        },
                        "&:active": {
                            transform: "scale(0.95)",
                        },
                        transition: "all 150ms ease-in-out",
                    }}
                >
                    <Add sx={{ fontSize: 20 }} />
                </IconButton>
            </Paper>

            {/* Scrollable Questions Tree */}
            <Box
                sx={{
                    flex: 1,
                    height: "calc(100vh - 180px)",
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch",
                    px: 1,
                    py: 1,
                    pb: "80px",
                }}
            >
                <QuestionsTree
                    onSelectionChange={(keys) => onSelectionChange(keys)}
                    id_project={onCreateProject.id}
                    isMobile={isMobile}
                    mode={mode}
                    setActiveIndex={setActiveIndex}
                    handleAddDefaultProjectForm={handleAddDefaultProjectForm}
                />
            </Box>

            {/* Sticky Bottom Navigation */}
            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    px: 2.5,
                    py: 2,
                    pb: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                    background: "linear-gradient(to top, rgba(255,255,255,0.92) 50%, rgba(255,255,255,0.78) 100%)",
                    backdropFilter: "blur(18px) saturate(1.6)",
                    WebkitBackdropFilter: "blur(18px) saturate(1.6)",
                    borderTop: "1px solid rgba(255, 144, 21, 0.15)",
                    boxShadow: "0 -4px 24px rgba(255, 144, 21, 0.06), 0 -1px 6px rgba(0, 0, 0, 0.04)",
                    zIndex: 10,
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    onClick={() => setActiveIndex(activeIndex + 1)}
                    sx={{
                        borderRadius: "14px",
                        textTransform: "none",
                        minHeight: 50,
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        letterSpacing: "-0.01em",
                        background: "linear-gradient(135deg, #FF9015 0%, #FFA940 100%)",
                        backgroundColor: "#FF9015",
                        color: "#fff",
                        border: "none",
                        boxShadow: "0 4px 16px rgba(255, 144, 21, 0.40), 0 8px 32px rgba(255, 144, 21, 0.20), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                        transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                            background: "linear-gradient(135deg, #FF9015 0%, #FFB74D 100%)",
                            backgroundColor: "#FF9015",
                            boxShadow: "0 6px 24px rgba(255, 144, 21, 0.50), 0 12px 40px rgba(255, 144, 21, 0.25)",
                            transform: "translateY(-2px)",
                        },
                        "&:active": {
                            transform: "scale(0.98)",
                            boxShadow: "0 2px 10px rgba(255, 144, 21, 0.35)",
                        },
                    }}
                >
                    <Langs str="Next" />
                </Button>
            </Box>
        </Box>
    );
}
